// START IMPORTS
// Hooks imports
import { useReport } from '@ltvco/refresh-lib/hooks';
import { Box, Stack } from '@ltvco/refresh-lib/theme';
import {
  DateUtil,
  isZeroed,
  useScrollToSectionOnNavigate,
} from '@ltvco/refresh-lib/utils';
import {
  EmailOverview,
  ReportOptions,
  ReportLoading,
  ReportMonitor,
  useSession,
  DebugMenu,
  EmailFraudCheckSection,
} from '@ltvco/refresh-lib/v1';
import {
  ReportFactory,
  AddressHistorySection,
  AssetsSection,
  EducationSection,
  EmailSection,
  JobsSection,
  NotesSection,
  PhoneSection,
  PotentialOwners,
  RelatedReportsSection,
  ReportActionsWithDateUpdated,
  ReportChangesOverview,
  ReportNavigationMenu,
  ReportNavigationMenuProps,
  ReportNavigationMobileMenu,
  ReportRating,
  ReportNullState,
  SocialSection,
  ReportClaimingSection,
  MonitoringCard,
  MonitoringBannerSection,
  type NavLinkDataV2,
} from '@ltvco/refresh-lib/v2';
// Theme and UI imports
// Utility imports
// Core lib v1 imports
// Core lib v2 imports
import { useFeatureIsOn } from '@ltvco/refresh-lib/vendors';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';

// App-specific imports
import { constants } from 'appConstants';
import { getEmailNavLinkDataV2 } from 'navLinkData/emailNavLinkData';

import { getReportSections } from './utils/getReportSections';

// Type imports
import type { ReportRouteProps } from './types';
// END IMPORTS

// START INTERFACES_AND_TYPES
interface EmailReportProps extends ReportRouteProps {}
// END INTERFACES_AND_TYPES

// START CONSTANTS
const reportType = 'email';
// END CONSTANTS

export function EmailReportPage({
  permalink,
  isMonitored: _unusedInitialMonitored = false,
}: EmailReportProps) {
  // START HOOKS_AND_QUERIES
  const queryResult = useReport(permalink);
  const { currentMonitors, reportIsMonitored } = queryResult;

  const {
    session: { account },
  } = useSession();

  const currentUserInfo = account?.account?.user_info;
  const { hash, search } = useLocation();

  const zeroed = isZeroed(queryResult);
  useScrollToSectionOnNavigate(hash, queryResult);
  // END HOOKS_AND_QUERIES

  // START STATE_MANAGEMENT
  const [reportOptions, setReportOptions] = useState<ReportOptions>({
    potentialOwnerIndex: 0,
    showHighConfidenceDataOnly: true,
    higherConfidenceThreshold: constants.config.higherConfidenceThreshold,
  });
  const [activeClaiming, setActiveClaiming] = useState(false);
  // END STATE_MANAGEMENT

  // START EVENT_HANDLERS
  const handleSetReportOptions = (newOptions: Partial<ReportOptions>) => {
    setReportOptions({ ...reportOptions, ...newOptions });
  };
  // END EVENT_HANDLERS

  // START EFFECTS
  useEffect(() => {
    setActiveClaiming(
      currentMonitors.some((monitor: ReportMonitor) => {
        return monitor.permalink === permalink && monitor.is_claimed;
      })
    );
  }, [currentMonitors, permalink]);
  // END EFFECTS

  // START EARLY_RETURNS
  if (queryResult.isLoading || queryResult.isError) {
    return <ReportLoading menuItems={10} />;
  }
  if (zeroed) {
    return <ReportNullState />;
  }

  const report = ReportFactory.create(queryResult?.data, 'email');

  if (!report) {
    return <ReportLoading menuItems={10} />;
  }

  if (!report?.data?.people && !report?.data?.people?.length) {
    return <ReportNullState />;
  }
  // END EARLY_RETURNS

  // START DATA_PREPARATION
  const showReportClaiming =
    currentUserInfo?.email?.toLowerCase() ===
    report.emailAddress?.toLowerCase();

  const ClaimingComponent = showReportClaiming ? (
    <ReportClaimingSection
      reportPermaLink={permalink}
      reportType={reportType}
      reportIsMonitoredAndClaimed={activeClaiming}
    />
  ) : reportIsMonitored ? null : (
    <MonitoringBannerSection
      reportPermaLink={permalink}
      reportType={reportType}
    />
  );

  const MonitoringComponent = (
    <MonitoringCard permalink={permalink} reportType={reportType} />
  );

  const owner = report.getOwner(reportOptions);
  const {
    ownerName,
    emails,
    phones,
    educations,
    jobs,
    profiles,
    usernames,
    addresses,
    ownedAssets,
    userIds,
    relatives,
  } = owner;

  const { supportingEmail } = report;

  const { fraud_score, deliverability, recent_abuse, is_leaked } =
    supportingEmail || {};

  const emailNavLinkDataV2 = getEmailNavLinkDataV2(
    report?.data.people?.length || 0,
    owner
  );

  const queryParams = new URLSearchParams(search);
  const fallbackEmail = queryParams.get('email') || '';
  const lowerCaseEmailAddress =
    report?.emailAddress?.toLowerCase() ?? fallbackEmail;
  document.title = `${lowerCaseEmailAddress} - NumberGuru`;

  const date = new DateUtil();
  const reportUpdateDate = report.data.meta?.updated_at
    ? date.parseDateFromString(
        report.data.meta?.updated_at,
        'yyyy-MM-dd',
        'yyyy-MM-dd HH:mm:ss ZZZ'
      )
    : '';
  const isEmailPartialAddress = useFeatureIsOn('OAR_1707');
  const alternativeTitle = isEmailPartialAddress
    ? 'Possible Locations'
    : undefined;

  const reportSections = getReportSections(
    emailNavLinkDataV2 as NavLinkDataV2[]
  );
  // END DATA_PREPARATION

  // START RENDER
  return (
    <Box>
      <Stack direction="row">
        {/* START DESKTOP_NAVIGATION */}
        <Box
          sx={{
            display: { xs: 'none', sm: 'none', md: 'none', lg: 'block' },
          }}
        >
          <ReportNavigationMenu
            reportType="Email Report"
            headerTitle={lowerCaseEmailAddress}
            menuItems={
              emailNavLinkDataV2 as ReportNavigationMenuProps['menuItems']
            }
          />
        </Box>
        {/* END DESKTOP_NAVIGATION */}

        {/* START MAIN_CONTENT */}
        <Box marginTop={0}>
          {/* START REPORT_CHANGES_OVERVIEW */}
          {reportIsMonitored && (
            <ReportChangesOverview
              permalink={permalink}
              navLinkData={emailNavLinkDataV2 as unknown as NavLinkDataV2[]}
            />
          )}
          {/* END REPORT_CHANGES_OVERVIEW */}

          {/* START REPORT_ACTIONS */}
          <>
            <Box
              sx={{
                height: 30,
                backgroundColor: 'background.default',
                position: 'sticky',
                marginBottom: '-10px',
                marginX: -1,
                top: 52,
                zIndex: 10,
                display: {
                  xs: 'none',
                  sm: 'block',
                  md: 'block',
                  lg: 'block',
                },
              }}
            />

            <ReportActionsWithDateUpdated
              reportType={reportType}
              reportTitle="Email"
              dateUpdated={reportUpdateDate}
              reportUpgraded={false}
            />
          </>
          {/* END REPORT_ACTIONS */}

          {/* START REPORT_OVERVIEW */}
          <EmailOverview
            reportType={reportType}
            email={lowerCaseEmailAddress}
            showHighConfidenceToggle
            showHighConfidenceDataOnly={
              reportOptions.showHighConfidenceDataOnly
            }
            setShowHighConfidenceDataOnly={(value: boolean) => {
              handleSetReportOptions({
                showHighConfidenceDataOnly: value,
              });
            }}
            personName={ownerName || lowerCaseEmailAddress}
            emailOverviewItemsData={{
              ownerName,
              emails,
              jobs,
              educations,
              phones,
              profiles,
              usernames,
              userIds,
              peopleCount: report.data.people?.length,
            }}
            Monitoring={MonitoringComponent}
          />
          {/* END REPORT_OVERVIEW */}
          {ClaimingComponent}
          {/* START FRAUD_CHECK_SECTION */}
          <EmailFraudCheckSection
            fraudScore={fraud_score}
            deliverability={deliverability}
            abusiveBehavior={recent_abuse}
            leaked={is_leaked}
            showEmailFraudScan={supportingEmail != undefined}
            emailAddress={lowerCaseEmailAddress}
            showReportCTA
          />
          {/* END FRAUD_CHECK_SECTION */}
          {/* START POTENTIAL_OWNERS_SECTION */}
          <PotentialOwners
            potentialOwners={report.data.people || []}
            potentialOwnerIndex={reportOptions.potentialOwnerIndex}
            setPotentialOwnerIndex={(index: number) => {
              handleSetReportOptions({ potentialOwnerIndex: index });
            }}
            personName={ownerName || lowerCaseEmailAddress}
            permalink={permalink}
            reportType={reportType}
            reportSections={reportSections}
          />
          {/* END POTENTIAL_OWNERS_SECTION */}

          {/* START PERSONAL_INFO_SECTIONS */}
          <PhoneSection
            personName={ownerName || lowerCaseEmailAddress}
            phoneList={phones}
            permalink={permalink}
          />
          <EmailSection
            personName={ownerName || lowerCaseEmailAddress}
            emailList={emails}
            permalink={permalink}
          />
          <AddressHistorySection
            addresses={addresses}
            personName={ownerName || lowerCaseEmailAddress}
            permalink={permalink}
            alternateTitle={alternativeTitle}
          />
          <SocialSection
            personName={ownerName || lowerCaseEmailAddress}
            profiles={profiles}
            usernames={usernames}
            permalink={permalink}
          />
          {/* END PERSONAL_INFO_SECTIONS */}
          {/* START ADDITIONAL_INFO_SECTIONS */}
          <JobsSection
            personName={ownerName || lowerCaseEmailAddress}
            jobs={jobs}
            permalink={permalink}
          />
          <EducationSection
            personName={ownerName || lowerCaseEmailAddress}
            educations={educations}
            permalink={permalink}
          />
          <AssetsSection
            personName={ownerName || lowerCaseEmailAddress}
            ownedAssets={ownedAssets}
            permalink={permalink}
          />
          <NotesSection permalink={permalink} />
          <RelatedReportsSection
            reportType="email"
            personName={ownerName || lowerCaseEmailAddress}
            potentialOwners={report?.data?.people}
            relatives={relatives}
            addresses={addresses}
            phones={phones}
            emails={emails}
            usernames={usernames}
          />
          <ReportRating
            report_type={reportType}
            rating={report.data.meta?.rating || null}
          />
          {/* END ADDITIONAL_INFO_SECTIONS */}
        </Box>
        {/* END MAIN_CONTENT */}
      </Stack>

      {/* START MOBILE_NAVIGATION */}
      <ReportNavigationMobileMenu
        reportType="Email Report"
        headerTitle={lowerCaseEmailAddress}
        menuItems={emailNavLinkDataV2 as ReportNavigationMenuProps['menuItems']}
        isMobileBannerShowed={false}
      />
      {/* END MOBILE_NAVIGATION */}

      {/* START DEBUG_MENU */}
      <DebugMenu menuItems={report.data.rawData.debug_menu} />
      {/* END DEBUG_MENU */}
    </Box>
  );
  // END RENDER
}
